import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Upload, X, Image as ImageIcon, Save, LogOut, Eye, Layout, Type,
  BarChart3, Home, Mountain, Building2, Compass, Layers, Thermometer,
  MapPin, Award, Gift, Users, TrendingUp, Megaphone, Phone, FileText,
  Images, MessageSquare, CheckCircle2, Clock, Mail, ChevronRight,
  Trash2, Plus, ExternalLink, RefreshCw, AlertCircle, Search
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = `${API}/api/uploads/navanaa_logo_clean.png`;
const SITE_URL = window.location.origin;

const SECTION_CONFIG = {
  hero:         { label: 'Hero Section',       icon: Home,        desc: 'Main banner with headline, subtitle & background image' },
  stats:        { label: 'Statistics Bar',     icon: BarChart3,   desc: 'Key numbers displayed below the hero' },
  about:        { label: 'About Section',      icon: FileText,    desc: 'About Navanaa with description & values' },
  langha:       { label: 'Langha Life',        icon: Mountain,    desc: 'Location overview with tags & image' },
  estate:       { label: 'The Estate',         icon: Building2,   desc: 'Project details with stats & image' },
  architecture: { label: 'Architecture',       icon: Compass,     desc: 'Design language points & gallery images' },
  materials:    { label: 'Materials',          icon: Layers,      desc: 'Material palette cards (stone, timber, clay, glass)' },
  floorplans:   { label: 'Floor Plans',        icon: Layout,      desc: 'Ground & first floor room breakdown' },
  systems:      { label: 'Smart Systems',      icon: Thermometer, desc: 'AI & smart home features' },
  location:     { label: 'Location',           icon: MapPin,      desc: 'Connectivity, dining, essentials & video' },
  difference:   { label: 'The Difference',     icon: Award,       desc: 'What makes Navanaa unique + comparison' },
  amenities:    { label: 'Amenities',          icon: Gift,        desc: 'What the home includes by category' },
  propman:      { label: 'Property Mgmt',      icon: Users,       desc: 'Staff, optional services & sustainability' },
  lifestyle:    { label: 'Lifestyle',          icon: Users,       desc: 'Day-in-the-life moments & target audience' },
  investment:   { label: 'Investment',         icon: TrendingUp,  desc: 'Value proposition & scarcity' },
  cta:          { label: 'Call to Action',     icon: Megaphone,   desc: 'CTA banner headline & subtitle' },
  contact:      { label: 'Contact Info',       icon: Phone,       desc: 'Email, phone, location & RERA' },
  footer:       { label: 'Footer',            icon: FileText,    desc: 'Brand name, tagline & copyright' },
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function isImageField(key, value) {
  const imageKeys = ['image', 'image_url', 'img', 'photo', 'thumbnail', 'logo', 'bg', 'background'];
  const lk = key.toLowerCase();
  if (imageKeys.some(k => lk === k || lk.endsWith(`_${k}`) || lk.startsWith(`${k}_`))) return true;
  if (typeof value === 'string' && value && (
    value.match(/\.(jpg|jpeg|png|webp|gif|svg)(\?|$)/i) || value.startsWith('/api/uploads/')
  )) return true;
  return false;
}

function isVideoField(key, value) {
  const lk = key.toLowerCase();
  if (lk === 'video' || lk === 'video_url' || lk.endsWith('_video')) return true;
  if (typeof value === 'string' && value.match(/\.(mp4|mov|webm|avi)(\?|$)/i)) return true;
  return false;
}

function friendlyLabel(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function imgUrl(val) {
  if (!val) return null;
  return val.startsWith('/') ? `${API}${val}` : val;
}

// ─── IMAGE UPLOADER ──────────────────────────────────────────────────────────

function ImageUploader({ value, onChange, label, token }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/api/upload`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange(res.data.url);
    } catch (err) { alert('Upload failed. Please try again.'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) doUpload(file);
  };

  const src = imgUrl(value);

  return (
    <div className="cms-field" data-testid={`image-field-${label}`}>
      <label className="cms-label">
        <ImageIcon size={13} /> {friendlyLabel(label)}
      </label>
      {src ? (
        <div className="cms-img-box">
          <img src={src} alt="" className="cms-img-preview" />
          <div className="cms-img-bar">
            <button className="cms-btn-sm cms-btn-gold" onClick={() => fileRef.current?.click()} disabled={uploading}>
              <Upload size={13} /> {uploading ? 'Uploading...' : 'Replace'}
            </button>
            <button className="cms-btn-sm cms-btn-danger" onClick={() => onChange('')}>
              <Trash2 size={13} /> Remove
            </button>
            <a href={src} target="_blank" rel="noopener noreferrer" className="cms-btn-sm cms-btn-ghost">
              <ExternalLink size={13} /> View
            </a>
          </div>
        </div>
      ) : (
        <div
          className={`cms-dropzone ${dragOver ? 'active' : ''}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <Upload size={28} />
          <span>{uploading ? 'Uploading...' : 'Drop image here or click to browse'}</span>
          <small>JPG, PNG, WebP, GIF, SVG</small>
        </div>
      )}
      <input type="file" ref={fileRef} accept="image/*" onChange={e => doUpload(e.target.files[0])} style={{ display: 'none' }} />
    </div>
  );
}

// ─── SECTION EDITOR ──────────────────────────────────────────────────────────

function SectionEditor({ sectionId, content, token, onSave }) {
  const [data, setData] = useState(content || {});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => { setData(content || {}); setSuccess(''); setHasChanges(false); }, [content, sectionId]);

  const update = (key, val) => { setData(prev => ({ ...prev, [key]: val })); setHasChanges(true); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/sections/${sectionId}`, { content: data }, { withCredentials: true });
      setSuccess('Changes saved successfully!');
      setHasChanges(false);
      onSave();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { alert('Save failed'); }
    setSaving(false);
  };

  const renderField = (key, val, updateFn, prefix = '', depth = 0) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (isImageField(key, val)) {
      return <ImageUploader key={fullKey} label={key} value={val || ''} onChange={v => { updateFn(key, v); setHasChanges(true); }} token={token} />;
    }

    if (isVideoField(key, val)) {
      return (
        <div key={fullKey} className="cms-field">
          <label className="cms-label">{friendlyLabel(key)} (Video)</label>
          <input className="cms-input" type="text" value={val || ''} onChange={e => { updateFn(key, e.target.value); setHasChanges(true); }} placeholder="Paste video URL..." />
          {val && <video src={val} controls muted className="cms-video-preview" />}
        </div>
      );
    }

    if (typeof val === 'string') {
      const TEXTAREA_KEYS = ['body','body1','body2','body3','desc','description','subtitle','quote','location','tagline','copyright','message'];
      const isLong = val.length > 80 || val.includes('\n') || TEXTAREA_KEYS.some(k => key.toLowerCase().includes(k));
      return (
        <div key={fullKey} className="cms-field">
          <label className="cms-label"><Type size={13} /> {friendlyLabel(key)}</label>
          {isLong ? (
            <textarea className="cms-textarea" value={val} onChange={e => { updateFn(key, e.target.value); setHasChanges(true); }} rows={Math.max(3, val.split('\n').length + 1)} />
          ) : (
            <input className="cms-input" type="text" value={val} onChange={e => { updateFn(key, e.target.value); setHasChanges(true); }} />
          )}
        </div>
      );
    }

    if (typeof val === 'number') {
      return (
        <div key={fullKey} className="cms-field">
          <label className="cms-label">{friendlyLabel(key)}</label>
          <input className="cms-input" type="number" value={val} onChange={e => { updateFn(key, parseInt(e.target.value) || 0); setHasChanges(true); }} />
        </div>
      );
    }

    if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object') {
      return (
        <div key={fullKey} className="cms-group">
          <div className="cms-group-header">
            <h4>{friendlyLabel(key)} <span className="cms-badge">{val.length} items</span></h4>
          </div>
          {val.map((item, idx) => (
            <div key={idx} className="cms-array-card">
              <div className="cms-array-card-head">
                <span>#{idx + 1}</span>
                {item.title && <span className="cms-array-card-title">{item.title}</span>}
                {item.name && <span className="cms-array-card-title">{item.name}</span>}
                {item.time && <span className="cms-array-card-title">{item.time}</span>}
              </div>
              <div className="cms-array-card-body">
                {Object.entries(item).map(([ik, iv]) => {
                  const itemUpdate = (field, newVal) => {
                    const arr = [...val]; arr[idx] = { ...arr[idx], [field]: newVal }; updateFn(key, arr); setHasChanges(true);
                  };
                  if (isImageField(ik, iv)) return <ImageUploader key={`${fullKey}.${idx}.${ik}`} label={`${key}_${idx}_${ik}`} value={iv || ''} onChange={v => itemUpdate(ik, v)} token={token} />;
                  if (typeof iv === 'string') {
                    const long = iv.length > 60 || ik.includes('desc') || ik.includes('body');
                    return (
                      <div className="cms-field" key={`${fullKey}.${idx}.${ik}`}>
                        <label className="cms-label-sm">{friendlyLabel(ik)}</label>
                        {long ? <textarea className="cms-textarea" value={iv} onChange={e => itemUpdate(ik, e.target.value)} rows={2} /> : <input className="cms-input" value={iv} onChange={e => itemUpdate(ik, e.target.value)} />}
                      </div>
                    );
                  }
                  if (Array.isArray(iv) && iv.length > 0 && typeof iv[0] === 'string') {
                    return (
                      <div className="cms-field" key={`${fullKey}.${idx}.${ik}`}>
                        <label className="cms-label-sm">{friendlyLabel(ik)} (one per line)</label>
                        <textarea className="cms-textarea" value={iv.join('\n')} onChange={e => itemUpdate(ik, e.target.value.split('\n'))} rows={3} />
                      </div>
                    );
                  }
                  if (Array.isArray(iv)) {
                    return (
                      <div className="cms-field" key={`${fullKey}.${idx}.${ik}`}>
                        <label className="cms-label-sm">{friendlyLabel(ik)} (JSON)</label>
                        <textarea className="cms-textarea" value={JSON.stringify(iv)} onChange={e => { try { itemUpdate(ik, JSON.parse(e.target.value)); } catch (_e) { /* user still typing JSON */ } }} rows={2} />
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
      return (
        <div key={fullKey} className="cms-field">
          <label className="cms-label">{friendlyLabel(key)} (one per line)</label>
          <textarea className="cms-textarea" value={val.join('\n')} onChange={e => { updateFn(key, e.target.value.split('\n')); setHasChanges(true); }} rows={Math.min(val.length + 1, 8)} />
        </div>
      );
    }

    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      return (
        <div key={fullKey} className="cms-group">
          <div className="cms-group-header"><h4>{friendlyLabel(key)}</h4></div>
          <div className="cms-group-body">
            {Object.entries(val).map(([nk, nv]) => {
              const nestedUpdate = (field, newVal) => { updateFn(key, { ...val, [field]: newVal }); setHasChanges(true); };
              return renderField(nk, nv, nestedUpdate, fullKey, depth + 1);
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  const config = SECTION_CONFIG[sectionId];

  return (
    <div className="cms-editor" data-testid={`section-editor-${sectionId}`}>
      {config && (
        <div className="cms-editor-desc">
          <AlertCircle size={14} /> {config.desc}
        </div>
      )}
      <div className="cms-editor-fields">
        {Object.entries(data).map(([key, val]) => renderField(key, val, update))}
      </div>
      <div className={`cms-save-bar ${hasChanges ? 'visible' : ''}`}>
        <div className="cms-save-bar-inner">
          {success ? (
            <span className="cms-save-msg"><CheckCircle2 size={15} /> {success}</span>
          ) : hasChanges ? (
            <span className="cms-save-msg unsaved"><AlertCircle size={15} /> You have unsaved changes</span>
          ) : null}
          <button className="cms-save-btn" onClick={save} disabled={saving || !hasChanges} data-testid="admin-save-btn">
            {saving ? <><RefreshCw size={14} className="spin" /> Saving...</> : <><Save size={14} /> Save Changes</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── GALLERY MANAGER ─────────────────────────────────────────────────────────

function GalleryManager({ token }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', image_url: '' });
  const [uploading, setUploading] = useState(false);
  const [adding, setAdding] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const res = await axios.get(`${API}/api/gallery`); setItems(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/api/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(p => ({ ...p, image_url: res.data.url }));
    } catch { alert('Upload failed'); }
    setUploading(false);
  };

  const add = async () => {
    if (!form.title || !form.image_url) return alert('Title and image are required');
    setAdding(true);
    try {
      await axios.post(`${API}/api/gallery`, { ...form, order: items.length }, { withCredentials: true });
      setForm({ title: '', description: '', image_url: '' });
      load();
    } catch { alert('Failed'); }
    setAdding(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this gallery image?')) return;
    await axios.delete(`${API}/api/gallery/${id}`, { withCredentials: true });
    load();
  };

  return (
    <div data-testid="gallery-manager">
      <div className="cms-stat-row">
        <div className="cms-mini-stat"><Images size={18} /><span>{items.length}</span><small>Gallery Images</small></div>
      </div>

      <div className="cms-group" style={{ marginTop: 24 }}>
        <div className="cms-group-header"><h4><Plus size={15} /> Add New Image</h4></div>
        <div className="cms-group-body" style={{ display: 'grid', gridTemplateColumns: form.image_url ? '300px 1fr' : '1fr', gap: 24 }}>
          {form.image_url ? (
            <div className="cms-img-box">
              <img src={imgUrl(form.image_url)} alt="" className="cms-img-preview" style={{ maxHeight: 200 }} />
              <div className="cms-img-bar">
                <button className="cms-btn-sm cms-btn-gold" onClick={() => fileRef.current?.click()}><Upload size={12} /> Replace</button>
                <button className="cms-btn-sm cms-btn-danger" onClick={() => setForm({ ...form, image_url: '' })}><X size={12} /></button>
              </div>
            </div>
          ) : (
            <div className="cms-dropzone" onClick={() => fileRef.current?.click()} style={{ minHeight: 140 }}>
              <Upload size={24} /><span>{uploading ? 'Uploading...' : 'Upload gallery image'}</span>
            </div>
          )}
          <div>
            <div className="cms-field"><label className="cms-label">Title</label><input className="cms-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Estate Front View" data-testid="gallery-title-input" /></div>
            <div className="cms-field"><label className="cms-label">Caption (optional)</label><input className="cms-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Short description..." /></div>
            <button className="cms-save-btn" onClick={add} disabled={adding || !form.title || !form.image_url} data-testid="gallery-add-btn" style={{ marginTop: 8 }}>
              {adding ? 'Adding...' : <><Plus size={14} /> Add to Gallery</>}
            </button>
          </div>
        </div>
        <input type="file" ref={fileRef} accept="image/*" onChange={e => handleUpload(e.target.files[0])} style={{ display: 'none' }} />
      </div>

      <div className="cms-gallery-grid" data-testid="gallery-grid">
        {items.map(item => (
          <div className="cms-gallery-card" key={item.id}>
            <div className="cms-gallery-card-img">
              <img src={imgUrl(item.image_url)} alt={item.title} />
            </div>
            <div className="cms-gallery-card-info">
              <h5>{item.title}</h5>
              {item.description && <p>{item.description}</p>}
            </div>
            <button className="cms-gallery-delete" onClick={() => remove(item.id)} data-testid={`gallery-delete-${item.id}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="cms-empty">No gallery images yet. Add one above.</p>}
      </div>
    </div>
  );
}

// ─── CONTACTS VIEWER ─────────────────────────────────────────────────────────

function ContactsViewer({ token }) {
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    const res = await axios.get(`${API}/api/contacts`, { withCredentials: true });
    setContacts(res.data);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await axios.put(`${API}/api/contacts/${id}/read`, {}, { withCredentials: true });
    setContacts(prev => prev.map(c => c.id === id ? { ...c, read: true } : c));
  };

  const unread = contacts.filter(c => !c.read).length;
  const filtered = contacts.filter(c => {
    if (filter === 'unread' && c.read) return false;
    if (filter === 'read' && !c.read) return false;
    if (search) {
      const s = search.toLowerCase();
      return c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s) || c.message.toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div data-testid="contacts-viewer">
      <div className="cms-stat-row">
        <div className="cms-mini-stat"><MessageSquare size={18} /><span>{contacts.length}</span><small>Total Enquiries</small></div>
        <div className="cms-mini-stat highlight"><AlertCircle size={18} /><span>{unread}</span><small>Unread</small></div>
        <div className="cms-mini-stat"><CheckCircle2 size={18} /><span>{contacts.length - unread}</span><small>Read</small></div>
      </div>

      <div className="cms-contacts-toolbar">
        <div className="cms-filter-tabs">
          {['all', 'unread', 'read'].map(f => (
            <button key={f} className={`cms-filter-tab ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f === 'unread' ? `Unread (${unread})` : 'Read'}
            </button>
          ))}
        </div>
        <div className="cms-search-box">
          <Search size={14} />
          <input placeholder="Search by name, email or message..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="cms-btn-sm cms-btn-ghost" onClick={load}><RefreshCw size={13} /> Refresh</button>
      </div>

      <div className="cms-contacts-list" data-testid="contacts-list">
        {filtered.length === 0 ? (
          <div className="cms-empty">
            {search ? 'No results match your search.' : filter === 'unread' ? 'All caught up! No unread enquiries.' : 'No contact submissions yet.'}
          </div>
        ) : filtered.map(c => (
          <div className={`cms-contact-card ${!c.read ? 'unread' : ''}`} key={c.id} data-testid={`contact-${c.id}`}>
            <div className="cms-contact-status">
              {!c.read ? <span className="cms-dot pulse" /> : <CheckCircle2 size={14} className="cms-contact-read-icon" />}
            </div>
            <div className="cms-contact-main">
              <div className="cms-contact-header">
                <h5>{c.name}</h5>
                <span className="cms-contact-time"><Clock size={12} /> {new Date(c.created_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })} at {new Date(c.created_at).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })}</span>
              </div>
              <div className="cms-contact-meta">
                <a href={`mailto:${c.email}`}><Mail size={12} /> {c.email}</a>
                {c.phone && <span><Phone size={12} /> {c.phone}</span>}
              </div>
              <div className="cms-contact-message">{c.message}</div>
            </div>
            <div className="cms-contact-actions">
              {!c.read && (
                <button className="cms-btn-sm cms-btn-gold" onClick={() => markRead(c.id)}>
                  <CheckCircle2 size={13} /> Mark Read
                </button>
              )}
              <a href={`mailto:${c.email}`} className="cms-btn-sm cms-btn-ghost"><Mail size={13} /> Reply</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN DASHBOARD ──────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  const [sections, setSections] = useState({});
  const [contacts, setContacts] = useState([]);
  const [gallery, setGallery] = useState([]);

  // Verify auth via httpOnly cookie (no localStorage)
  useEffect(() => {
    axios.get(`${API}/api/admin/me`, { withCredentials: true })
      .then(() => setAuthenticated(true))
      .catch(() => navigate('/admin'));
  }, [navigate]);

  const loadAll = useCallback(async () => {
    if (!authenticated) return;
    try {
      const [secRes, conRes, galRes] = await Promise.all([
        axios.get(`${API}/api/sections`),
        axios.get(`${API}/api/contacts`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/gallery`).catch(() => ({ data: [] })),
      ]);
      setSections(secRes.data);
      setContacts(conRes.data);
      setGallery(galRes.data);
    } catch (_e) { /* network error during load */ }
  }, [authenticated]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const logout = async () => {
    try { await axios.post(`${API}/api/admin/logout`, {}, { withCredentials: true }); } catch (_e) { /* ok */ }
    navigate('/admin');
  };

  const unread = contacts.filter(c => !c.read).length;

  if (!authenticated) return null;

  // Pass withCredentials token via cookie — components use this for uploads
  const token = 'cookie';

  const currentConfig = SECTION_CONFIG[activeTab];
  const CurrentIcon = currentConfig?.icon || FileText;
  const title = activeTab === 'gallery' ? 'Gallery Manager' : activeTab === 'contacts' ? 'Contact Submissions' : currentConfig?.label || activeTab;

  return (
    <div className="cms-page" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="cms-sidebar">
        <div className="cms-sidebar-top">
          <img src={LOGO_URL} alt="Navanaa" className="cms-sidebar-logo" />
          <span className="cms-sidebar-badge">CMS</span>
        </div>

        <div className="cms-sidebar-section-label">Website Sections</div>
        {Object.entries(SECTION_CONFIG).map(([id, cfg]) => {
          const Icon = cfg.icon;
          return (
            <button key={id} className={`cms-nav-item ${activeTab === id ? 'active' : ''}`} onClick={() => setActiveTab(id)} data-testid={`admin-nav-${id}`}>
              <Icon size={15} /> <span>{cfg.label}</span>
            </button>
          );
        })}

        <div className="cms-sidebar-section-label">Manage</div>
        <button className={`cms-nav-item ${activeTab === 'gallery' ? 'active' : ''}`} onClick={() => setActiveTab('gallery')} data-testid="admin-nav-gallery">
          <Images size={15} /> <span>Gallery</span>
          <span className="cms-nav-count">{gallery.length}</span>
        </button>
        <button className={`cms-nav-item ${activeTab === 'contacts' ? 'active' : ''}`} onClick={() => setActiveTab('contacts')} data-testid="admin-nav-contacts">
          <MessageSquare size={15} /> <span>Enquiries</span>
          {unread > 0 && <span className="cms-nav-badge">{unread}</span>}
        </button>

        <div className="cms-sidebar-bottom">
          <a href={SITE_URL} target="_blank" rel="noopener noreferrer" className="cms-nav-item cms-view-site">
            <Eye size={15} /> <span>View Website</span> <ExternalLink size={12} />
          </a>
          <button className="cms-nav-item cms-logout-btn" onClick={logout} data-testid="admin-logout-btn">
            <LogOut size={15} /> <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="cms-main">
        <header className="cms-topbar">
          <div className="cms-topbar-left">
            <CurrentIcon size={20} />
            <div>
              <h1>{title}</h1>
              {currentConfig && <p>{currentConfig.desc}</p>}
              {activeTab === 'gallery' && <p>Manage your estate gallery images</p>}
              {activeTab === 'contacts' && <p>View and manage visitor enquiries</p>}
            </div>
          </div>
          <div className="cms-topbar-right">
            <a href={SITE_URL} target="_blank" rel="noopener noreferrer" className="cms-btn-sm cms-btn-ghost">
              <Eye size={13} /> Preview Site
            </a>
          </div>
        </header>

        <div className="cms-body">
          {activeTab === 'gallery' ? (
            <GalleryManager token={token} />
          ) : activeTab === 'contacts' ? (
            <ContactsViewer token={token} />
          ) : sections[activeTab] ? (
            <SectionEditor sectionId={activeTab} content={sections[activeTab]} token={token} onSave={loadAll} />
          ) : (
            <div className="cms-empty">Loading section data...</div>
          )}
        </div>
      </main>
    </div>
  );
}
